import { useCallback, useState } from 'react';
import { useConfigValuesState } from '../../hooks/useConfigValue';

/**
 * A custom React hook to manage time and calendar configuration settings.
 *
 * This hook provides functionality to:
 * - Manage and validate the default daily capacity value (0-24 hours)
 * - Handle user input changes and validation
 * - Update the configuration in the global state
 *
 * @returns {Object} Returns an object containing:
 *  - `defaultCapacity` (string) - The current default capacity value
 *  - `onChange` (function) - Handler for input changes
 *  - `onBlur` (function) - Validation handler when input loses focus
 *  - `error` (string) - Error message if validation fails
 *
 * @example
 * const TimeConfig = () => {
 *   const { defaultCapacity, onChange, onBlur, error } = useTimeAndCalendarGridConfig();
 *
 *   return (
 *     <TextField
 *       value={defaultCapacity}
 *       onChange={onChange}
 *       onBlur={onBlur}
 *       errorMessage={error}
 *     />
 *   );
 * };
 */
export function useTimesheetGridConfig() {
    const {
        initialData: {
            configuration: {
                showAllocations: showAllocationsLocal,
                showOnlyAllocatedProjects: showOnlyAllocatedProjectsLocal,
                highlightAllocatedProjects: highlightAllocatedProjectsLocal,
            },
        },
        setConfigValue,
    } = useConfigValuesState();
    const [showAllocations, setShowAllocations] = useState<boolean>(showAllocationsLocal);
    const [showOnlyAllocatedProjects, setShowOnlyAllocatedProjects] = useState<boolean>(showOnlyAllocatedProjectsLocal);
    const [highlightAllocatedProjects, setHighlightAllocatedProjects] = useState<boolean>(highlightAllocatedProjectsLocal);

    const onChangeShowAllocations = (checked: boolean) => {
        setShowAllocations(checked);
        updateShowAllocations(checked);
    };

    const onChangeShowOnlyAllocatedProjects = (checked: boolean) => {
        setShowOnlyAllocatedProjects(checked);
        updateShowOnlyAllocatedProjects(checked);
    };

    const onChangeHighlightAllocatedProjects = (checked: boolean) => {
        setHighlightAllocatedProjects(checked);
        updateShowHighlightAllocatedProjects(checked);
    };

    const updateShowAllocations = useCallback(
        (newValue: boolean) => {
            setConfigValue(currentConfig => ({
                ...currentConfig,
                showAllocations: newValue,
            }));
        },
        [setConfigValue],
    );

    const updateShowOnlyAllocatedProjects = useCallback(
        (newValue: boolean) => {
            setConfigValue(currentConfig => ({
                ...currentConfig,
                showOnlyAllocatedProjects: newValue,
            }));
        },
        [setConfigValue],
    );

    const updateShowHighlightAllocatedProjects = useCallback(
        (newValue: boolean) => {
            setConfigValue(currentConfig => ({
                ...currentConfig,
                highlightAllocatedProjects: newValue,
            }));
        },
        [setConfigValue],
    );

    return {
        showAllocations,
        showOnlyAllocatedProjects,
        highlightAllocatedProjects,
        onChangeShowAllocations,
        onChangeShowOnlyAllocatedProjects,
        onChangeHighlightAllocatedProjects,
    };
}
